import {graphql} from 'gatsby';
import React, {Fragment, memo, useEffect, useMemo, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';

import {styleMods, createNavigationUrlApple, createNavigationUrlGoogle} from '../utils';
import {cmpByKey, cmpReverse} from '../utils/cmp';

import * as iter from '../utils/iter';

import * as option from '../utils/option';

import Link from './link';

import Columns, {Column} from './columns';
import HidableBox from './hidable-box';
import ImageCarousel from './image-carousel';
import IntlString, { intlString } from './intl-string';
import IntlStringHtml from './intl-string-html';
import style from './map-artist.module.scss';
import RatioImage from './ratio-image';

/**
 * @param {{children: React.ReactNode, fullWidth?: boolean}} props
 * @returns {React.ReactElement} ...
 */
function ArtistBlock({children, fullWidth}) {
	return (<div className={styleMods(style, 'block', fullWidth && 'fullWidth')}>{children}</div>);
}

export const query = graphql`
	fragment MapArtist on ArtistsJson {
		slug
		name {
			...IntlString
		}
		projectNumber
		projectName {
			...IntlString
		}
		projectAudioDescription {
			local {
				publicURL
			}
			mimetype
		}
		locationImage {
			...RatioImage
		}
		location {
			...IntlString
		}
		gender
		aboutArtistsWork {
			...IntlStringHtml
		}
		aboutArtist {
			...IntlStringHtml
		}
		otherProjects {
			year
			text {
				...IntlString
			}
		}
		projectImages {
			...ImageCarousel
		}
		geo
		geoSecondary
	}
`;

export default memo(
	/**
	 * @param {{
	 *    open: boolean,
	 *    toggleOpen: () => void,
	 *    artist: import("../../tmp/graphql-types").MapArtistFragment,
	 *    openAudio: boolean,
	 *    selectMapTarget: () => void,
	 *  }} props
	 * @returns {React.ReactElement} ...
	 **/
	function MapArtists({
		open,
		toggleOpen,
		artist,
		openAudio,
		selectMapTarget,
	}) {
		const intl = useIntl();

		const [appleNavigator, setAppleNavigator] = useState(false);

		useEffect(
			() => {
				if (/(Mac|iPhone|iPod|iPad)/i.test(navigator.platform)) {
					setAppleNavigator(true);
				}
			},
			[],
		);

		/** @type {React.MutableRefObject<HTMLSpanElement | null>} */
		const anchorRef = useRef(null);

		/** @type {React.MutableRefObject<HTMLSpanElement | null>} */
		const anchorAudioRef = useRef(null);

		const audioVisible = intl.locale === "de";

		useEffect(
			() => {
				if (!open) {
					return undefined;
				}

				const handle = setTimeout(
					() => {
						if (openAudio && audioVisible && anchorAudioRef.current) {
							anchorAudioRef.current.scrollIntoView({
								behavior: 'smooth',
								block: 'start',
								inline: 'nearest',
							});
						} else if (anchorRef.current) {
							anchorRef.current.scrollIntoView({
								behavior: 'smooth',
								block: 'start',
								inline: 'nearest',
							});
						}
					},
					500,
				);

				return () => {
					clearTimeout(handle);
				};
			},
			[open, openAudio, audioVisible],
		);

		const otherProjects2 = useMemo(
			() => artist.otherProjects
					.sort(cmpReverse(cmpByKey(({year}) => year)))
					.map(({year, text}, i) => (
						<div key={i} className={style.otherProjectsYearGroup}>
							<dt className={style.otherProjectsYear}>{year}</dt>

							<dd className={style.otherProjectsText}>
								<IntlString handleLines={true} {...text} />
							</dd>
						</div>
					)),
			[artist.otherProjects],
		);

		const navLinkRes = iter.chain(
			iter.from(artist.geo || []),
			iter.from(artist.geoSecondary || []),
		)
			.map(
				/**
				 * @returns {null | [number, number]} ...
				 */
				(geo) => {
					if (
						'geometry' in geo
						&& 'coordinates' in geo.geometry
						&& Array.isArray(geo.geometry.coordinates)
						&& 2 <= geo.geometry.coordinates.length
					) {
						const x = parseFloat(geo.geometry.coordinates[0]);
						const y = parseFloat(geo.geometry.coordinates[1]);
						if (!isNaN(x) && !isNaN(y)) {
							return [x, y];
						}
					}
					return null;
				}
			)
			.filter(Boolean)
			.map(option.unwrap)
			.map((coords) => {
				const name = `${artist.projectNumber} ${intlString(intl, artist.name)} - ${intlString(intl, artist.projectName)}`;
				if (appleNavigator) {
					return createNavigationUrlApple(name, coords);
				} else {
					return createNavigationUrlGoogle(name, coords);
				}
			})
			.map((url) => {
				return (
					<Link
						href={url}
						addExternalIcon
						className={style.openInNavAppLink}
					>
						<FormattedMessage id="artist.openInNavApp" />
					</Link>
				);
			})
			.next();
		const navLink = navLinkRes.done ? null : navLinkRes.value;

		return (
			<article className={style.base}>
				<span
					id={artist.slug}
					className={style.anchor}
					ref={anchorRef}
				/>

				<HidableBox
					visible={open}
					toggleVisible={toggleOpen}
					header={(
						<div className={style.header}>
							<div className={style.nameProjectName}>
								<h1 className={style.name}>
									<IntlString {...artist.name} />
								</h1>

								<h2 className={style.projectName}>
									<IntlString {...artist.projectName} />
								</h2>
							</div>

							<h2 className={style.number}>
								{artist.projectNumber}
							</h2>
						</div>
					)}
				>
					<div className={style.body}>
						<ArtistBlock fullWidth={true}>
							<ImageCarousel images={artist.projectImages} />
						</ArtistBlock>

						<ArtistBlock>
							<h3 className={style.bodyHeadline}>
								<FormattedMessage
									id="artist.aboutArtistsWork"
									values={{gender: artist.gender}}
								/>
							</h3>

							<Columns
								columns={[
									new Column({width: 5}, (
										<div className={style.aboutArtistsWork}>
											<IntlStringHtml {...artist.aboutArtistsWork} />
										</div>
									)),
									new Column({width: 2},
										(artist.locationImage || navLink) && (
											<Fragment>
												<div
													role="button"
													tabIndex={0}
													onClick={selectMapTarget}
													className={style.locationLink}
												>
													<figure
														className={style.locationImageFigure}
													>
														{artist.locationImage && (
															<RatioImage
																{...artist.locationImage}
																title={artist.location}
															/>
														)}
														<figcaption
															className={style.locationImageCaption}
														>
															<IntlString {...artist.location} />
														</figcaption>
													</figure>
												</div>

												{navLink}
											</Fragment>
										)
									),
								]}
							/>

							{open && audioVisible && artist.projectAudioDescription?.local?.publicURL && (
								<Fragment>
									<span
										id={`${artist.slug}-audio`}
										className={style.anchor}
										ref={anchorAudioRef}
									/>

									<h3 className={style.bodyHeadline}>
										Audioguide
									</h3>

									<audio
										controls={true}
										preload="metadata"
										className={style.projectAudioDescription}
									>
										<source
											src={artist.projectAudioDescription.local.publicURL}
											type={artist.projectAudioDescription.mimetype}
										/>
									</audio>
								</Fragment>
							)}
						</ArtistBlock>

						<ArtistBlock>
							<Columns
								columns={[
									new Column({width: 2}, (
										<Fragment>
											<h3 className={style.bodyHeadline}>
												<FormattedMessage
													id="artist.aboutArtist"
													values={{gender: artist.gender}}
												/>
											</h3>
											<div className={style.aboutArtist}>
												<IntlStringHtml {...artist.aboutArtist} />
											</div>
										</Fragment>
									)),
									new Column({width: 5},
										otherProjects2.length > 0 && (
											<Fragment>
												<h3 className={style.bodyHeadline}>
													<FormattedMessage
														id="artist.expositions"
														values={{gender: artist.gender}}
													/>
												</h3>

												<dl className={style.otherProjectsList}>
													{otherProjects2}
												</dl>
											</Fragment>
										)
									),
								]}
							/>
						</ArtistBlock>
					</div>
				</HidableBox>
			</article>
		);
	}
);
